<vsf-collection-breadcrumbs [breadcrumbs]="(collection$ | async)?.breadcrumbs"></vsf-collection-breadcrumbs>
<div class="max-w-7xl mx-2 xl:mx-auto pt-2 pb-24 lg:grid lg:grid-cols-3 lg:gap-x-8 xl:grid-cols-4 border-t">
    <div class="mt-6 lg:mt-0 lg:col-span-3 xl:col-span-4 lg:mx-4">
        <div class="text-center my-6">
            <h3 class="text-3xl text-gray-600" *ngIf="isSearchPage$ | async">
                Results for "{{ searchTerm$ | async }}"
            </h3>
        </div>

        <div *ngIf="collection$ | async as collection; else headingPlaceholder">
            <h1 class="text-3xl font-bold tracking-tight text-gray-900">
                {{ collection.name }}
            </h1>

            <ng-container *ngIf="subCollectionsNonExcluded$ | async as subCollections">
                <div *ngIf="subCollections.length" class="border-b mt-4">
                    <div
                        class="grid items-baseline grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7 xl:grid-cols-9"
                    >
                        <ng-container *ngFor="let child of subCollections">
                            <a
                                [routerLink]="['/c', child.slug | canonicalCollectionSlug]"
                                class="p-2 flex flex-col items-center justify-center mb-4 rounded hover:bg-gray-50"
                            >
                                <kb-picture
                                    [src]="child.featuredAsset?.preview"
                                    [focalPoint]="child.featuredAsset?.focalPoint"
                                    preset="thumb"
                                    imgClass="rounded h-12 w-12 sm:h-20 sm:w-20"
                                ></kb-picture>
                                <div class="text-sm text-gray-600 text-center mt-2">{{ child.name }}</div>
                            </a>
                        </ng-container>
                    </div>
                </div>
            </ng-container>

            <kb-tabbed-description
                class="block mt-6"
                [description]="collection.description"
                [alwaysCreateDescriptionTab]="false"
                [descriptionCutoffLength]="400"
            ></kb-tabbed-description>
            <div *ngIf="collection.customFields.filterPresets.length" class="my-3 md:my-6">
                <div
                    class="border-t border-secondary-600 border-dotted md:max-w-2xl mx-auto flex justify-around my-4"
                >
                    <p class="-mt-3 text-secondary-700 bg-white px-1">Popular filters</p>
                </div>
                <div class="grid grid-cols-2 sm:grid-cols-3 md:flex-wrap md:flex md:justify-center">
                    <kb-filter-preset-link
                        class="block m-1 md:m-3"
                        *ngFor="let preset of collection.customFields.filterPresets | sortByPosition"
                        [preset]="preset"
                        [activeFacetValues]="facetFilterService.activeFacetValueIds$ | async"
                        [availableFacetValues]="facetFilterService.facetsWithValues$ | async"
                    ></kb-filter-preset-link>
                </div>
            </div>
        </div>
        <ng-template #headingPlaceholder>
            <div *ngIf="loading$ | async" class="animate-pulse">
                <div class="w-60 h-8 bg-gray-200 rounded"></div>
                <div class="w-full h-2 bg-gray-200 rounded mt-6"></div>
                <div class="w-1/2 h-2 bg-gray-200 rounded mt-2"></div>
            </div>
        </ng-template>

        <div class="mt-4 min-h-[85px]">
            <div class="z-10 pt-2 bg-gray-50">
                <kbart-product-list-filters-horizontal
                    [class.lg:hidden]="(totalResults$ | async) === 0"
                    class="hidden md:block mt-2"
                    [facets]="facetFilterService.facetsWithValues$ | async"
                    [activeFacetValueIds]="facetFilterService.activeFacetValueIds$ | async"
                    [featureFacetIds]="featuredFacetIds$ | async"
                    [excludeFacetIds]="excludedFacetIds$ | async"
                    [collectionSlug]="(collection$ | async)?.slug"
                    (close)="mobileFiltersExpanded = false"
                ></kbart-product-list-filters-horizontal>

                <div
                    class="flex flex-col lg:flex-row justify-between p-2 border-b border-t border-dotted bg-gray-100"
                >
                    <div class="flex">
                        <!-- Mobile filter dialog toggle, controls the 'mobileFilterDialogOpen' state. -->
                        <button
                            type="button"
                            class="btn btn-icon inline-flex bg-gray-50 hover:bg-gray-100 border-gray-300 items-center md:hidden mr-2"
                            (click)="mobileFiltersExpanded = true"
                        >
                            <fa-icon [icon]="filter" class="text-gray-400 mr-1" size="1x"></fa-icon>
                            <span class="text-sm font-medium text-gray-700">Filters</span>
                        </button>
                        <kb-active-filters
                            [facets]="facetFilterService.facetsWithValues$ | async"
                            [collection]="searchFilterCollection$ | async"
                            [groupedActiveFacetValueIds]="
                                facetFilterService.groupedActiveFacetValueIds$ | async
                            "
                            [activeFacetValueIds]="facetFilterService.activeFacetValueIds$ | async"
                            [prices]="priceMinMax$ | async"
                        ></kb-active-filters>
                    </div>

                    <div class="flex justify-between items-baseline space-x-4">
                        <span
                            class="text-sm font-medium text-gray-600"
                            *ngIf="totalResults$ | async as resultCount"
                        >
                            {{ resultCount }} products
                        </span>
                        <ng-container *ngIf="sortOrder$ | async as sortOrder">
                            <kbart-combo-box [items]="sortOrders" (itemClick)="setSortOrder($event.order)">
                                <div kbartComboBoxLabel>
                                    <ng-container *ngIf="sortOrder === 'default'; else nonDefault"
                                        >Sort</ng-container
                                    >
                                    <ng-template #nonDefault>{{ getSortOrderLabel(sortOrder) }}</ng-template>
                                </div>
                                <ng-template let-item="item" #comboBoxItem>
                                    <fa-icon
                                        [icon]="check"
                                        *ngIf="sortOrder === item.order"
                                        class="mr-2"
                                    ></fa-icon>
                                    <span>{{ item.label }}</span>
                                </ng-template>
                            </kbart-combo-box>
                        </ng-container>
                    </div>
                </div>
            </div>
        </div>
        <ng-container *ngIf="(loading$ | async) && !(totalResults$ | async)">
            <div class="-mx-px grid grid-cols-2 sm:mx-0 md:grid-cols-3 lg:grid-cols-4">
                <vsf-product-card
                    *ngFor="let product of placeholderProducts"
                    [product]="product"
                ></vsf-product-card>
            </div>
        </ng-container>
        <ng-container *ngIf="totalResults$ | async">
            <div class="-mx-px grid grid-cols-2 sm:mx-0 md:grid-cols-3 lg:grid-cols-4">
                <vsf-product-card
                    [ngClass]="{ 'opacity-40': loading$ | async }"
                    class="block transition-opacity"
                    *ngFor="
                        let product of products$ | async | paginate: (paginationConfig$ | async);
                        index as i;
                        trackBy: trackByProductId
                    "
                    [product]="product"
                    [lazyLoadImg]="12 < i"
                    (productClick)="logResultClick(product.searchResultId, i + 1)"
                ></vsf-product-card>
            </div>
            <kb-pagination (pageChange)="setCurrentPage($event)"></kb-pagination>
        </ng-container>

        <ng-container *ngIf="(totalResults$ | async) === 0">
            <div class="pt-24 flex flex-col items-center">
                <p class="text-4xl text-gray-400 mb-12">No results</p>
                <fa-icon class="text-gray-300" [icon]="sadCry" size="10x"></fa-icon>
            </div>
        </ng-container>

        <div *ngIf="extendedDescription$ | async as extendedDescription" class="my-24">
            <h3 class="text-2xl mb-4 text-gray-600">More information</h3>
            <div [innerHTML]="extendedDescription" class="prose"></div>
        </div>

        <ng-container *ngIf="subCollectionsExcluded$ | async as subCollections">
            <div *ngIf="subCollections.length" class="my-8 flex flex-wrap gap-4">
                <p>Additional categories:</p>
                <ng-container *ngFor="let child of subCollections">
                    <a
                        [routerLink]="['/c', child.slug | canonicalCollectionSlug]"
                        class="text-link"
                    >{{ child.name }}
                    </a>
                </ng-container>
            </div>
        </ng-container>
    </div>
    <kb-side-tray
        [expanded]="mobileFiltersExpanded"
        trayClasses="max-w-2xl"
        (close)="mobileFiltersExpanded = false"
    >
        <vsf-product-list-filters
            class="block lg:hidden mx-4"
            [facets]="facetFilterService.facetsWithValues$ | async"
            [activeFacetValueIds]="facetFilterService.activeFacetValueIds$ | async"
            [featureFacetIds]="featuredFacetIds$ | async"
            [excludeFacetIds]="excludedFacetIds$ | async"
            (close)="mobileFiltersExpanded = false"
        ></vsf-product-list-filters>
    </kb-side-tray>
</div>
